import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  useColorModeValue,
  useToast,
  Alert,
  AlertIcon,
  Link,
  Flex,
  Image,
} from "@chakra-ui/react";
import ReCAPTCHA from "react-google-recaptcha";
import React, { useState, useRef } from "react";
import axios from "axios";
import BackGroundImage from "../../resources/PlatformBackround.png"
import eduviclogo from "../../resources/Eduvictechlogo.png";

interface B2CLoginProps {
  onLogin: (token: string , refresh_token:string) => void;
}

const B2CLogin: React.FC<B2CLoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState<string | null>(null);
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  // ── ADDED: forgot password state only ──
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const toast = useToast();
  const apiUrl = process.env.REACT_APP_API_URL;

  // ── EXACTLY AS ORIGINAL ──
  const recaptchaKey = process.env.REACT_APP_GOOGLE_CAPTCHA_KEY;

  if (!recaptchaKey) {
    throw new Error('REACT_APP_GOOGLE_CAPTCHA_KEY is not defined');
  }

  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  // ── EXACTLY AS ORIGINAL ──
  const handleRecaptchaChange = (value: string | null) => {
    setCaptchaValue(value);
    setRecaptchaError(null);
  };

  const handleRecaptchaError = () => {
    setRecaptchaError("ReCAPTCHA error. Please try again.");
  };

  const handleLogin = async () => {
    if (!captchaValue) {
      setRecaptchaError("Please complete the CAPTCHA.");
      return;
    }

    setIsLoading(true);
    try {
      const formData = new URLSearchParams();
      formData.append("username", email);
      formData.append("password", password);
      formData.append("recaptcha_token", captchaValue);   // NEW

     const response = await axios.post(`${apiUrl}/b2c/login`, formData, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });

      onLogin(response.data.access_token, response.data.refresh_token);
    } catch (error: any) {
      toast({
        title: error.response?.data?.detail ?? "Login failed.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
      recaptchaRef.current?.reset();   // NEW: Google tokens are single use
      setCaptchaValue(null);           // NEW
    }
  };


  // ── ADDED: forgot password handler only ──
  const handleForgotPassword = async () => {
    if (!forgotEmail.trim()) {
      toast({ title: "Please enter your email", status: "warning", duration: 3000 });
      return;
    }
    setForgotLoading(true);
    try {
      await axios.post(`${apiUrl}/b2c/forgot-password`, {
        email: forgotEmail.trim().toLowerCase(),
      });
    } catch {
      // swallow -- backend always returns generic response
    } finally {
      setForgotSent(true);
      setForgotLoading(false);
    }
  };

  return (
            <Box
      bgImage={`url(${BackGroundImage})`}
      bgSize="cover"
      bgPosition="center"
      minHeight="100vh"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      px={4}
      pt="72px"
    >


      <Box
        maxW="400px"
        mx="auto"
        mt={10}
        p={8}
        borderRadius="lg"
        boxShadow="lg"
        bg={bg}
        borderWidth="1px"
        borderColor={borderColor}
      >
        {/* ── ADDED: forgot password view wraps everything.
            When showForgotPassword is false the original JSX
            below is rendered completely unchanged ── */}
        {showForgotPassword ? (
          <Box>
                  {/* Logo */}
            <Flex justify="center" mb={6}>
              <Box
              boxSize={{ base: "240px", md: "190px" }}
              borderRadius="full"
              backgroundColor="blue.800"
              display="flex"
              alignItems="center"
              justifyContent="center"
              overflow="hidden"
              >
              <Image
                  src={eduviclogo}
                  alt="Eduvic logo"
                  maxW="85%"
                  maxH="85%"
                  objectFit="contain"
              />
              </Box>
            </Flex>

          <VStack spacing={5} align="stretch">
            <Text fontSize="xl" fontWeight="bold">Reset Password</Text>
            <Text fontSize="sm" color="gray.500">
              Enter your registered email and we'll send you a reset link.
            </Text>

            {forgotSent ? (
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                If an account with that email exists, a reset link has been sent.
                Please check your inbox — the link expires in 30 minutes.
              </Alert>
            ) : (
              <>
                <FormControl>
                  <FormLabel fontSize="sm">Email</FormLabel>
                  <Input
                    type="email"
                    value={forgotEmail}
                    onChange={e => setForgotEmail(e.target.value)}
                    placeholder="you@example.com"
                    onKeyDown={e => { if (e.key === "Enter") handleForgotPassword(); }}
                  />
                </FormControl>
                <Button
                  colorScheme="purple"
                  width="100%"
                  isLoading={forgotLoading}
                  loadingText="Sending..."
                  onClick={handleForgotPassword}
                >
                  Send Reset Link
                </Button>
              </>
            )}

            <Link
              fontSize="sm"
              color="gray.500"
              textAlign="center"
              cursor="pointer"
              onClick={() => {
                setShowForgotPassword(false);
                setForgotSent(false);
                setForgotEmail("");
              }}
            >
              ← Back to Sign In
            </Link>
          </VStack>

          </Box>

         ) : (

          <Box>
                  {/* Logo */}
            <Flex justify="center" mb={6}>
              <Box
              boxSize={{ base: "240px", md: "190px" }}
              borderRadius="full"
              backgroundColor="blue.800"
              display="flex"
              alignItems="center"
              justifyContent="center"
              overflow="hidden"
              >
              <Image
                  src={eduviclogo}
                  alt="Eduvic logo"
                  maxW="85%"
                  maxH="85%"
                  objectFit="contain"
              />
              </Box>
            </Flex>

         { /* ── ORIGINAL login JSX, not one character changed ── */}
          <VStack spacing={4}>
            <Text fontSize="2xl" fontWeight="bold">
              Student Login
            </Text>
            <FormControl id="email">
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
            </FormControl>
            <FormControl id="password">
              <FormLabel>Password</FormLabel>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
              />
            </FormControl>

            <VStack mt={5} spacing={4}>
              {recaptchaError && <Text color="red.500">{recaptchaError}</Text>}
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={recaptchaKey}
                onChange={handleRecaptchaChange}
                onErrored={handleRecaptchaError}
              />
            </VStack>

            <Button
              colorScheme="purple"
              width="full"
              mt={4}
              onClick={handleLogin}
              isLoading={isLoading}
            >
              Login
            </Button>

            {/* ── ADDED: forgot password link only ── */}
            <Link
              fontSize="sm"
              color="purple.500"
              cursor="pointer"
              onClick={() => {
                setShowForgotPassword(true);
                setForgotEmail(email);
                setForgotSent(false);
              }}
            >
              Forgot your password?
            </Link>
          </VStack>

          </Box>

        )}
      </Box>
        </Box>
  );
};

export default B2CLogin;